import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,

	},
	tokenPaper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(2),
		marginBottom: 12,
	},
	tokenField: {
		flex: 1,
	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},

}));

const Settings = () => {
	const classes = useStyles();
	const timezoneOptions = [
		{ value: "America/Sao_Paulo", label: "Brasília (America/Sao_Paulo)" },
		{ value: "America/Fortaleza", label: "Fortaleza (America/Fortaleza)" },
		{ value: "America/Belem", label: "Belém (America/Belem)" },
		{ value: "America/Cuiaba", label: "Cuiabá (America/Cuiaba)" },
		{ value: "America/Manaus", label: "Manaus (America/Manaus)" },
		{ value: "America/Rio_Branco", label: "Rio Branco (America/Rio_Branco)" },
		{ value: "America/Noronha", label: "Fernando de Noronha (America/Noronha)" },
		{ value: "UTC", label: "UTC" },
	];

	const [settings, setSettings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [regeneratingToken, setRegeneratingToken] = useState(false);
	const [awayMinutesDraft, setAwayMinutesDraft] = useState("5");
	const [timezoneDraft, setTimezoneDraft] = useState("America/Sao_Paulo");

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex !== -1) {
						aux[settingIndex].value = data.setting.value;
						return aux;
					}

					return [data.setting, ...aux];
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	useEffect(() => {
		setAwayMinutesDraft(getSettingValue("userAwayMinutes") || "5");
		setTimezoneDraft(getSettingValue("companyTimezone") || "America/Sao_Paulo");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [settings]);

	const getSettingValue = key => {
		return settings.find(s => s.key === key)?.value;
	};

	const handleSaveAwayMinutes = async () => {
		const sanitizedValue = String(
			Math.max(1, Number(awayMinutesDraft) || 5)
		);

		if (sanitizedValue === (getSettingValue("userAwayMinutes") || "5")) {
			setAwayMinutesDraft(sanitizedValue);
			return;
		}

		try {
			await api.put("/settings/userAwayMinutes", {
				value: sanitizedValue,
			});
			setAwayMinutesDraft(sanitizedValue);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveTimezone = async value => {
		const sanitizedValue = value || "America/Sao_Paulo";

		if (sanitizedValue === (getSettingValue("companyTimezone") || "America/Sao_Paulo")) {
			setTimezoneDraft(sanitizedValue);
			return;
		}

		try {
			await api.put("/settings/companyTimezone", {
				value: sanitizedValue,
			});
			setTimezoneDraft(sanitizedValue);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleRegenerateApiToken = async () => {
		setRegeneratingToken(true);
		try {
			await api.post("/settings/userApiToken/regenerate");
			toast.success("Token de integração regenerado com sucesso.");
		} catch (err) {
			toastError(err);
		} finally {
			setRegeneratingToken(false);
		}
	};

	const userApiTokenValue = getSettingValue("userApiToken") || "";

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="sm">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				<Paper className={classes.tokenPaper}>
					<TextField
						id="api-token-setting"
						label="Token de integração"
						margin="dense"
						variant="outlined"
						fullWidth
						className={classes.tokenField}
						value={userApiTokenValue}
						helperText="Use este token para autenticar integrações externas na API."
						InputProps={{
							readOnly: true,
						}}
					/>
					<Button
						variant="contained"
						color="primary"
						onClick={handleRegenerateApiToken}
						disabled={loading || regeneratingToken}
					>
						{regeneratingToken ? "Gerando..." : "Regenerar token"}
					</Button>
				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="user-away-minutes-setting"
						label="Tempo para considerar ausente (minutos)"
						margin="dense"
						variant="outlined"
						type="number"
						fullWidth
						inputProps={{ min: 1 }}
						helperText="Depois desse tempo sem atividade, o atendente passa para ausente."
						value={awayMinutesDraft}
						onChange={e => setAwayMinutesDraft(e.target.value)}
						onBlur={handleSaveAwayMinutes}
						onKeyDown={e => {
							if (e.key === "Enter") {
								handleSaveAwayMinutes();
							}
						}}
						disabled={loading}
					/>
				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="company-timezone-setting"
						select
						label="Timezone do dashboard"
						margin="dense"
						variant="outlined"
						fullWidth
						helperText="Usado para ajustar o horário e o período dos gráficos da dashboard."
						value={timezoneDraft}
						onChange={e => {
							setTimezoneDraft(e.target.value);
							handleSaveTimezone(e.target.value);
						}}
						disabled={loading}
					>
						{timezoneOptions.map(option => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
				</Paper>

			</Container>
		</div>
	);
};

export default Settings;
